// Copyright 2019 Sorama B.V.

const userSettingMetadataKey = Symbol("UserSetting");
import { IWorkflowPropertyParser } from "./Interfaces/IWorkflowPropertyParser";

export class UserSettingSettings {
    settingsModuleKey?: string;
    toStringConverter?: (value: unknown) => string;
    fromStringConverter?: (str: string) => unknown;
    objectType?: string;
    parser?: IWorkflowPropertyParser<unknown>;
}

export function userSetting(settings?: UserSettingSettings): PropertyDecorator {
    return function (target: object, targetProperty: string) {
        settings = settings ? settings : new UserSettingSettings();

        // Default value for this key is the target class name
        if (!settings.settingsModuleKey) settings.settingsModuleKey = target.constructor.name;

        if (typeof target[targetProperty] === "undefined") {
            // Make sure the property is defined, or getAllWorkflowPropertyMetadata will miss this
            // one if it doesn't have a default value in the declaration.
            Object.defineProperty(target, targetProperty, { writable: true, enumerable: true, configurable: true });
        }

        const propertyName = Reflect.getOwnMetadata("design:type", target, targetProperty);
        if (propertyName && !settings.objectType) {
            settings.objectType = propertyName.name;
        }

        Reflect.defineMetadata(userSettingMetadataKey, settings, target, targetProperty);
    };
}

export function getUserSettingSettings(target: object, propertyName: string): UserSettingSettings {
    const key = Reflect.getOwnMetadata(userSettingMetadataKey, Object.getPrototypeOf(target), propertyName);
    return key ? key : Reflect.getOwnMetadata(userSettingMetadataKey, target, propertyName);
}

export function getAllUserSettings(target: object): { propertyName: string; settings: UserSettingSettings }[] {
    const result: { propertyName: string; settings: UserSettingSettings }[] = [];

    const properties = Object.getOwnPropertyNames(target);

    for (const propertyName of properties) {
        const userSettingSettings = getUserSettingSettings(target, propertyName);
        if (userSettingSettings) {
            result.push({ propertyName: propertyName, settings: userSettingSettings });
        }
    }

    // Also iterate over the rest
    for (const propertyName in target) {
        if (result.find((x) => x.propertyName == propertyName)) continue;

        const userSettingSettings = getUserSettingSettings(target, propertyName);
        if (userSettingSettings) {
            result.push({ propertyName: propertyName, settings: userSettingSettings });
        }
    }

    return result;
}
